import React, { useEffect, useState } from "react";
import apiClient from "../../../utils/apiClient";
import { Button } from "../../../components/ui/button";
import { Input } from "../../../components/ui/input";
import { Label } from "../../../components/ui/label";
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../../components/ui/select";
import { EditSymbolModal } from "./EditSymbolModal";
import { DeleteSymbolModal } from "./DeleteSymbolModal";
import { AddSymbolModal } from "./AddSymbolModal";
import server from "../../../utils/serverConfig";
import { Loader2 } from "lucide-react";
import toast from "react-hot-toast";




export default function SymbolUpdateData() {
  const productOptions = [
    { value: "pledge_plus", title: "pledge_plus" },
    { value: "pledge_plus_mini", title: "pledge_plus_mini" },
  ];

  const categoryOptions = [
    { value: "commodity", title: "commodity" },
    { value: "index", title: "index" },
  ];

  const tableHeaders = ["Symbol", "Product", "Category", "Action"];

  const [symbolsList, setSymbolsList] = useState([]);
  const [isLoading, setIsLoading] = useState(true);




  // Category tab states for tables
  const [pledgePlusCategoryTab, setPledgePlusCategoryTab] = useState("index");
  const [pledgePlusMiniCategoryTab, setPledgePlusMiniCategoryTab] = useState("commodity");





  useEffect(() => {
    fetchSymbolsList();
  }, []);

  const fetchSymbolsList = (showLoader = true) => {
    if (showLoader) {
      setIsLoading(true);
    }
    let config = {
      method: "get",
      url: `${server.godseye.baseUrl}get-pledge-plus-symbols`,
    };

    apiClient
      .request(config)
      .then((response) => {
        console.log("Response symbols list:", response.data);
        if (response.data && response.data.data) {
          setSymbolsList(response.data.data);
        } else {
          setSymbolsList([]);
        }
        setIsLoading(false);
      })
      .catch((error) => {
        console.log("Error fetching symbols list:", error);
        setSymbolsList([]);
        setIsLoading(false);
      });
  };


  const handleAddSymbol = (symbolStr, product, category) => {
    const toastId = toast.loading("Adding symbol...");

    let config = {
      method: "POST",
      url: `${server.godseye.baseUrl}add-pledge-plus-symbol`,
      data: {
        symbol: symbolStr,
        product: product,
        category: category,
      },
    };

    apiClient
      .request(config)
      .then((response) => {
        console.log("Added symbol response:", response);
        if (response.data && response.data.error) {
          toast.error(response.data.error, { id: toastId });
        } else {
          toast.success("Symbol added successfully!", { id: toastId });
          fetchSymbolsList(false);
        }
      })
      .catch((error) => {
        console.log("Error adding symbol:", error);
        toast.error(error?.response?.data?.detail || "Failed to add symbol", { id: toastId });
      });
  };



  const handleUpdateSymbolValue = (
    oldSymbol,
    oldProduct,
    oldCategory,
    newSymbol,
    newProduct,
    newCategory
  ) => {
    const toastId = toast.loading("Updating symbol...");
    let config = {
      method: "POST",
      url: `${server.godseye.baseUrl}update-pledge-plus-symbol`,
      data: {
        oldSymbol: oldSymbol,
        oldProduct: oldProduct,
        oldCategory: oldCategory,
        symbol: newSymbol,
        product: newProduct,
        category: newCategory,
      },
    };

    apiClient
      .request(config)
      .then((response) => {
        console.log("Updated symbol response:", response);
        if (response.data && response.data.error) {
          toast.error(response.data.error, { id: toastId });
        } else {
          toast.success("Symbol updated successfully!", { id: toastId });
          fetchSymbolsList(false);
        }
      })
      .catch((error) => {
        console.log("Error updating symbol:", error);
        toast.error(error?.response?.data?.detail || "Failed to update symbol", { id: toastId });
      });
  };


  const handleRemoveSymbolFromList = (symbol, product, category) => {
    const toastId = toast.loading("Deleting symbol...");
    let config = {
      method: "POST",
      url: `${server.godseye.baseUrl}delete-pledge-plus-symbol`,
      data: {
        symbol: symbol,
        product: product,
        category: category,
      },
    };

    apiClient
      .request(config)
      .then((response) => {
        console.log("Deleted symbol response:", response);
        if (response.data && response.data.error) {
          toast.error(response.data.error, { id: toastId });
        } else {
          toast.success("Symbol deleted successfully!", { id: toastId });
          fetchSymbolsList(false);
        }
      })
      .catch((error) => {
        console.log("Error deleting symbol:", error);
        toast.error(error?.response?.data?.detail || "Failed to delete symbol", { id: toastId });
      });
  };


  // Client-side filtering logic
  const filteredSymbols = symbolsList;


  return (
    <div className="flex flex-col relative w-full h-full px-6 font-urbanist">
      <div className="flex items-center justify-between space-x-4">
        <div className="flex justify-between items-center h-[60px] w-full mb-4">
          <p className="flex items-center text-lg font-semibold text-white">
            Symbol Update Config Table
          </p>
          <AddSymbolModal handleAddSymbol={handleAddSymbol} />
        </div>
      </div>





      {/* Table view - Product Wise & Category Tabs */}
      {isLoading ? (
        <div className="flex flex-col items-center justify-center w-full h-[300px] space-y-4">
          <Loader2 className="h-8 w-8 animate-spin text-blue-500" />
          <p className="text-zinc-400 text-sm animate-pulse">Loading symbols...</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 w-full max-h-[calc(100vh-280px)] overflow-y-auto custom-scroll pb-6">
          
          {/* Pledge Plus Column */}
          <div className="flex flex-col border border-gray-800 bg-zinc-950/20 rounded-lg overflow-hidden h-fit">
            <div className="bg-zinc-900/50 px-4 py-2.5 border-b border-gray-800 flex justify-between items-center flex-wrap gap-2">
              <span className="text-white font-semibold tracking-wide">Pledge Plus (pledge_plus)</span>
              
              {/* Category Segment Tabs */}
              <div className="flex bg-[#121212] p-0.5 rounded-lg border border-zinc-800 text-xs">
                {["index", "commodity"].map((cat) => {
                  const count = filteredSymbols.filter(s => s.product === "pledge_plus" && s.category === cat).length;
                  return (
                    <button
                      key={cat}
                      onClick={() => setPledgePlusCategoryTab(cat)}
                      className={`px-3 py-1 rounded-md font-medium transition-all capitalize ${
                        pledgePlusCategoryTab === cat
                          ? "bg-blue-600 text-white shadow-lg"
                          : "text-gray-400 hover:text-white"
                      }`}
                    >
                      {cat} ({count})
                    </button>
                  );
                })}
              </div>
            </div>
            <div className="overflow-x-auto w-full">
              <table className="bg-black text-sm w-full">
                <thead className="bg-[#0F0F0F] text-xs text-left">
                  <tr className="text-white border-b border-gray-800">
                    <th className="border-r border-gray-800 px-4 py-2.5 font-semibold">Symbol</th>
                    <th className="px-4 py-2.5 font-semibold text-center">Action</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredSymbols.filter(s => s.product === "pledge_plus" && s.category === pledgePlusCategoryTab).length === 0 ? (
                    <tr>
                      <td colSpan={2} className="text-center text-zinc-500 py-8 font-medium">
                        No symbols found for {pledgePlusCategoryTab}
                      </td>
                    </tr>
                  ) : (
                    filteredSymbols
                      .filter(s => s.product === "pledge_plus" && s.category === pledgePlusCategoryTab)
                      .map((row, rowIndex) => (
                        <tr
                          key={rowIndex}
                          className="bg-black border-b last-of-type:border-b-0 border-gray-900 font-medium text-white hover:bg-black/90"
                        >
                          <td className="px-4 py-2 border-r border-gray-900">{row.symbol}</td>
                          <td className="text-center px-2 py-2">
                            <div className="flex items-center space-x-3 justify-center">
                              <EditSymbolModal
                                symbol={row.symbol}
                                product={row.product}
                                category={row.category}
                                handleUpdateSymbolValue={handleUpdateSymbolValue}
                              />
                              <DeleteSymbolModal
                                symbol={row.symbol}
                                product={row.product}
                                category={row.category}
                                handleRemoveSymbolFromList={handleRemoveSymbolFromList}
                              />
                            </div>
                          </td>
                        </tr>
                      ))
                  )}
                </tbody>
              </table>
            </div>
          </div>

          {/* Pledge Plus Mini Column */}
          <div className="flex flex-col border border-gray-800 bg-zinc-950/20 rounded-lg overflow-hidden h-fit">
            <div className="bg-zinc-900/50 px-4 py-2.5 border-b border-gray-800 flex justify-between items-center flex-wrap gap-2">
              <span className="text-white font-semibold tracking-wide">Pledge Plus Mini (pledge_plus_mini)</span>
              
              {/* Category Segment Tabs */}
              <div className="flex bg-[#121212] p-0.5 rounded-lg border border-zinc-800 text-xs">
                {["commodity", "index"].map((cat) => {
                  const count = filteredSymbols.filter(s => s.product === "pledge_plus_mini" && s.category === cat).length;
                  return (
                    <button
                      key={cat}
                      onClick={() => setPledgePlusMiniCategoryTab(cat)}
                      className={`px-3 py-1 rounded-md font-medium transition-all capitalize ${
                        pledgePlusMiniCategoryTab === cat
                          ? "bg-blue-600 text-white shadow-lg"
                          : "text-gray-400 hover:text-white"
                      }`}
                    >
                      {cat} ({count})
                    </button>
                  );
                })}
              </div>
            </div>
            <div className="overflow-x-auto w-full">
              <table className="bg-black text-sm w-full">
                <thead className="bg-[#0F0F0F] text-xs text-left">
                  <tr className="text-white border-b border-gray-800">
                    <th className="border-r border-gray-800 px-4 py-2.5 font-semibold">Symbol</th>
                    <th className="px-4 py-2.5 font-semibold text-center">Action</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredSymbols.filter(s => s.product === "pledge_plus_mini" && s.category === pledgePlusMiniCategoryTab).length === 0 ? (
                    <tr>
                      <td colSpan={2} className="text-center text-zinc-500 py-8 font-medium">
                        No symbols found for {pledgePlusMiniCategoryTab}
                      </td>
                    </tr>
                  ) : (
                    filteredSymbols
                      .filter(s => s.product === "pledge_plus_mini" && s.category === pledgePlusMiniCategoryTab)
                      .map((row, rowIndex) => (
                        <tr
                          key={rowIndex}
                          className="bg-black border-b last-of-type:border-b-0 border-gray-900 font-medium text-white hover:bg-black/90"
                        >
                          <td className="px-4 py-2 border-r border-gray-900">{row.symbol}</td>
                          <td className="text-center px-2 py-2">
                            <div className="flex items-center space-x-3 justify-center">
                              <EditSymbolModal
                                symbol={row.symbol}
                                product={row.product}
                                category={row.category}
                                handleUpdateSymbolValue={handleUpdateSymbolValue}
                              />
                              <DeleteSymbolModal
                                symbol={row.symbol}
                                product={row.product}
                                category={row.category}
                                handleRemoveSymbolFromList={handleRemoveSymbolFromList}
                              />
                            </div>
                          </td>
                        </tr>
                      ))
                  )}
                </tbody>
              </table>
            </div>
          </div>

        </div>
      )}
    </div>
  );
}


